import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  Pagination,
  Container,
  useTheme,
  CardActionArea,
  Skeleton,
} from "@mui/material";
import { motion } from "framer-motion";
import Navbar from "layouts/Homepage/navbar";
import Footer from "layouts/Homepage/Footer";
import MDTypography from "components/MDTypography";
import CoverLayout from "layouts/authentication/components/CoverLayout";

export default function BlogListPage() {
  const theme = useTheme();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(
        `https://blog.saleshive.cloud/wp-json/wp/v2/posts?_embed&per_page=6&page=${page}`
      )
      .then((res) => {
        setPosts(res.data);
        setTotalPages(parseInt(res.headers["x-wp-totalpages"], 10));
      })
      .catch((err) => console.error("Failed to load posts", err))
      .finally(() => setLoading(false));
  }, [page]);

  const shimmerArray = Array.from({ length: 6 });

  return (
    <CoverLayout>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <MDTypography
          variant="h2"
          gutterBottom
          fontWeight={700}
          textAlign="center"
          sx={{
            letterSpacing: 1,
            mb: 6,
            color: theme.palette.dark.main,
          }}
        >
          Latest Insights
        </MDTypography>

        <Grid container spacing={4}>
          {loading
            ? shimmerArray.map((_, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" height={32} width="80%" />
                      <Skeleton variant="text" height={20} width="90%" />
                      <Skeleton variant="text" height={20} width="70%" />
                      <Skeleton variant="text" height={36} width="30%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))
            : posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id} sx={{ display: "flex" }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    style={{ display: "flex", flex: 1, height: "100%" }}
                  >
                    <Card
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        boxShadow: 6,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: 12,
                        },
                      }}
                    >
                      <CardActionArea
                        component={Link}
                        to={`/blog/${post.slug}`}
                        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
                      >
                        {post._embedded?.["wp:featuredmedia"]?.[0]?.source_url && (
                          <Box sx={{ position: "relative" }}>
                            <CardMedia
                              component="img"
                              image={post._embedded["wp:featuredmedia"][0].source_url}
                              alt={post.title.rendered}
                              sx={{
                                width: "100%",
                                objectFit: "cover",
                                background: "#f7f7f7",
                                margin: "0 auto",
                                borderRadius: 0,
                                display: "block",
                             
                              }}
                            />
                          </Box>
                        )}

                        <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                          <MDTypography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: post.title.rendered,
                            }}
                          />
                          <MDTypography
                            variant="body2"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{
                              __html: post.excerpt.rendered,
                            }}
                            sx={{
                              mb: 2,
                              lineHeight: 1.5,
                              maxHeight: "5.5em",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                            }}
                          />
                          <Box flexGrow={1} />
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              color: theme.palette.info.main,
                            }}
                          >
                            Read More
                          </Button>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
        </Grid>

        {!loading && (
          <Box mt={6} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="info"
              shape="rounded"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "50%",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}
      </Container>
      <Footer />
    </CoverLayout>
  );
}
